import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Smartphone } from 'lucide-react';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  APP_STORE_LABEL,
  PLAY_STORE_LABEL,
  isLinkReady,
  isPlayStoreLink,
  isInternalLink,
} from '../../config/appLinks';

// Wiederverwendbare Store-Download-Buttons (Apple + Android).
// Links UND Beschriftung kommen aus config/appLinks.js.
// Leere Links => "Bald verfuegbar"-Fallback (kein toter Link).
//
// variant="compact" (default): kompakte Buttons (Onboarding, Banner)
// variant="large": groessere Buttons (Landing-Page Download-Section)
//
// Interne Ziele (z. B. /android) laufen über den Router im selben Tab, fremde
// Store-Adressen öffnen einen neuen Tab. internalInNewTab: auch interne Ziele in
// einem neuen Tab öffnen, wenn die aufrufende Seite stehen bleiben soll
// (Erfolgsseite des Profil-Setups).

const VARIANTS = {
  compact: {
    // Unter 380 px etwas enger, damit beide Knoepfe nebeneinander passen
    // (iPhone SE & Co.) statt untereinander umzubrechen.
    iconClass: 'w-5 h-5 min-[380px]:w-6 min-[380px]:h-6',
    wrapperGap: 'gap-2 min-[380px]:gap-3',
    button: 'gap-2 px-3.5 py-2.5 min-[380px]:gap-3 min-[380px]:px-5 min-[380px]:py-3 rounded-xl',
    activeShadow: 'shadow-token-md hover:shadow-token-md',
    fallbackBg: 'bg-gray-400/70 text-white',
    topClass: 'text-[10px]',
    bottomClass: 'text-sm min-[380px]:text-base',
    fallbackTopOpacity: 'opacity-80',
    hover: { scale: 1.05 },
  },
  large: {
    iconClass: 'w-7 h-7',
    wrapperGap: 'gap-4',
    button: 'gap-4 px-6 py-4 rounded-2xl',
    activeShadow: 'shadow-token-lg hover:shadow-token-xl',
    fallbackBg: 'bg-gray-700/60 text-white/70',
    topClass: 'text-[11px]',
    bottomClass: 'text-lg',
    fallbackTopOpacity: 'opacity-70',
    hover: { scale: 1.05, y: -2 },
  },
};

const AppleIcon = ({ className }) => (
  <svg viewBox="0 0 384 512" fill="currentColor" className={className} aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-27.1-46.9-42.2-83.7-45.3-35.1-3-73.4 20.5-87.5 20.5-14.8 0-49.1-19.5-74.3-19.5C63.1 140.2 0 185.3 0 284.9c0 29.3 5.3 59.6 15.9 90.8 14.2 41.3 65.3 142.6 118.6 140.9 27.8-.7 47.4-19.9 74.3-19.9 26.1 0 44.3 19.9 74.3 19.3 54.2-.9 99.5-91.3 113-132.7-71.9-34.2-67.4-100-67.4-115.6zM239.4 110.4c27.5-33.3 24.5-63.9 23.6-75-23.7 1.4-51.2 16.3-66.9 34.5-17.3 19.9-27.4 44.9-25.2 72.9 25.6 2 49.1-12 68.5-32.4z" />
  </svg>
);

// Play-Dreieck (vier Flaechen), einfarbig wie das Apple-Logo daneben.
const GooglePlayIcon = ({ className }) => (
  <svg viewBox="0 0 512 512" fill="currentColor" className={className} aria-hidden="true">
    <path d="M325.3 234.3 104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
  </svg>
);

// Erst beim ersten internen Knopf bauen, nicht beim Import: Tests, die
// framer-motion ohne motion.create nachbilden, laden die Datei sonst gar nicht.
let MotionLink = null;
const getMotionLink = () => {
  if (!MotionLink) MotionLink = motion.create(Link);
  return MotionLink;
};

const StoreButton = ({ href, icon, topText, bottomText, cfg, internalInNewTab = false }) => {
  // Link noch nicht gesetzt -> als deaktivierter "Bald verfuegbar"-Button anzeigen.
  if (!isLinkReady(href)) {
    return (
      <div className={`inline-flex items-center ${cfg.button} ${cfg.fallbackBg} shadow-inner cursor-not-allowed select-none`}>
        {icon}
        <div className="text-left leading-tight">
          <div className={`${cfg.topClass} uppercase tracking-wider ${cfg.fallbackTopOpacity}`}>{bottomText}</div>
          <div className={`${cfg.bottomClass} font-semibold`}>Bald verfügbar</div>
        </div>
      </div>
    );
  }

  const shared = {
    whileHover: cfg.hover,
    whileTap: { scale: 0.97 },
    className: `inline-flex items-center ${cfg.button} bg-gray-900 text-white ${cfg.activeShadow} transition-shadow duration-300`,
  };
  const content = (
    <>
      {icon}
      <div className="text-left leading-tight">
        <div className={`${cfg.topClass} uppercase tracking-wider opacity-80`}>{topText}</div>
        <div className={`${cfg.bottomClass} font-semibold`}>{bottomText}</div>
      </div>
    </>
  );

  // Eigene Seite: über den Router im selben Tab (kein neuer Tab für eine Anleitung).
  if (isInternalLink(href) && !internalInNewTab) {
    const InternalLink = getMotionLink();
    return <InternalLink to={href} {...shared}>{content}</InternalLink>;
  }

  return (
    <motion.a href={href} target="_blank" rel="noopener noreferrer" {...shared}>
      {content}
    </motion.a>
  );
};

const AppStoreButtons = ({ variant = 'compact', className = '', internalInNewTab = false }) => {
  const cfg = VARIANTS[variant] || VARIANTS.compact;
  return (
    <div className={`flex flex-row flex-wrap items-center justify-center ${cfg.wrapperGap} ${className}`}>
      <StoreButton
        href={APP_STORE_URL}
        icon={<AppleIcon className={cfg.iconClass} />}
        topText={APP_STORE_LABEL.top}
        bottomText={APP_STORE_LABEL.bottom}
        cfg={cfg}
      />
      <StoreButton
        href={PLAY_STORE_URL}
        // Play-Dreieck nur bei einem echten Play-Link; vor der Produktionsfreigabe
        // führt der Knopf auf die Anleitung /android, das Dreieck würde dort einen
        // öffentlichen Store-Eintrag behaupten, den es noch nicht gibt.
        icon={isPlayStoreLink(PLAY_STORE_URL)
          ? <GooglePlayIcon className={cfg.iconClass} />
          : <Smartphone className={cfg.iconClass} aria-hidden="true" />}
        topText={PLAY_STORE_LABEL.top}
        bottomText={PLAY_STORE_LABEL.bottom}
        cfg={cfg}
        internalInNewTab={internalInNewTab}
      />
    </div>
  );
};

export default AppStoreButtons;
